import React, { useEffect, useMemo, useState } from "react";
import { ButtonControl, DialogControl } from "spark-core-dx/components";
import { useGenericManager } from "spark-core-dx/hooks";
import { Operations } from "../../../services/ItemServices";
import { hasPendingClientOffer, isClientOfferActive, openMarketplaceCheckoutPanel } from "../marketplaceCheckout";

const CLIENT_RESOURCE_ITEM = "ClientResource";
const UNLIMITED_INCLUDED_USAGE_UNITS = 2147483647;
const EMPTY_CLIENT_PRODUCT_OFFERS = [];

const getFirst = (items) => {
  if (!items) return null;
  if (typeof items.first === "function") return items.first();
  return Array.isArray(items) ? items[0] : items;
};

const getFailureMessage = (result, fallback) => {
  const messages = result?.Messages || result?.messages || [];
  const message = getFirst(messages) || result?.Message || result?.message;
  return message?.DisplayMessage || message?.displayMessage || message?.Message || message?.message || fallback;
};

const normalizeQuantity = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (amount, currencyCode = "USD") => {
  const normalizedAmount = Number(amount || 0);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode || "USD",
    maximumFractionDigits: normalizedAmount % 1 === 0 ? 0 : 2
  }).format(normalizedAmount);
};

const getBillingPeriodLabel = (billingPeriod) => {
  switch (Number(billingPeriod || 0)) {
    case 1:
      return "month";
    case 2:
      return "year";
    case 3:
      return "one time";
    case 4:
      return "manual";
    default:
      return "period";
  }
};

const isUnlimitedOffer = (offer) => Number(offer?.offerKind || 0) === 3;

const buildPricingDisplay = (offer, isSelfHostedResourceMode = false) => {
  if (isSelfHostedResourceMode && isUnlimitedOffer(offer)) return "Included with Self-Hosted";
  if (offer?.reviewRequired) return "Review required";
  if (offer?.customPricing) return "Custom pricing";

  const baseAmount = Number(offer?.baseAmount || 0);
  const unitRate = Number(offer?.unitRate || 0);
  if (baseAmount > 0) {
    return `${formatCurrency(baseAmount, offer?.currencyCode)} / ${getBillingPeriodLabel(offer?.billingPeriod)}`;
  }
  if (unitRate > 0) {
    return `${formatCurrency(unitRate, offer?.currencyCode)} per ${offer?.unitLabel || "unit"}`;
  }
  return offer?.priceSummary || "Pricing managed on offer";
};

const buildGrantSummary = (offer, resourceKey) => {
  const grants = (offer?.resourceGrants || []).filter((grant) =>
    !resourceKey || grant.productResourceKey === resourceKey || grant.grantRole === 1
  );

  if (grants.length === 0) {
    return offer?.offerKind === 3 ? "Unlimited eligible resources" : "Manual package";
  }

  const relevantGrant = grants.find((grant) => grant.productResourceKey === resourceKey) || grants[0];
  const count = Number(relevantGrant?.allowedResourceConfigurationCount || 0);
  const label = count === 1 ? "configuration" : "configurations";
  const usageGrant = grants.find((grant) => {
    const includedUsageUnits = Number(grant.includedUsageUnits || 0);
    return grant.usagePricingEnabled && includedUsageUnits > 0 && includedUsageUnits < UNLIMITED_INCLUDED_USAGE_UNITS;
  });

  return usageGrant
    ? `${count || 1} ${label}, usage included`
    : `${count || 1} ${label}`;
};

const getOwnedQuantity = (clientProductOffers, offerKey) =>
  (clientProductOffers || [])
    .filter((clientOffer) => isClientOfferActive(clientOffer)
      && String(clientOffer.productOfferKey || "").toLowerCase() === String(offerKey || "").toLowerCase())
    .reduce((total, clientOffer) => total + normalizeQuantity(clientOffer.quantity || clientOffer.Quantity || 1), 0);

const getInitialOfferKey = ({ offers, requestedOfferKey, clientProductOffers, isSelfHostedResourceMode }) => {
  const requestedOffer = offers.find((offer) =>
    String(offer.key || "").toLowerCase() === String(requestedOfferKey || "").toLowerCase());
  if (requestedOffer) return requestedOffer.key;

  const selectableOffers = offers.filter((offer) => !(
    !isSelfHostedResourceMode
    && isUnlimitedOffer(offer)
    && getOwnedQuantity(clientProductOffers, offer?.key) >= 1
  ));
  return selectableOffers.length === 1 ? selectableOffers[0].key : "";
};

const MarketplaceAllowancePurchaseDialog = ({
  open = false,
  resource = null,
  product = null,
  environmentKey = "",
  clientProductOffers = EMPTY_CLIENT_PRODUCT_OFFERS,
  initialOfferKey = "",
  isSelfHostedResourceMode = false,
  beforeCheckout,
  onClose,
  onPurchased
}) => {
  const clientResourceMgr = useGenericManager(CLIENT_RESOURCE_ITEM);
  const [selectedOfferKey, setSelectedOfferKey] = useState(initialOfferKey || "");
  const [isSaving, setIsSaving] = useState(false);
  const [actionError, setActionError] = useState("");

  const offers = useMemo(() =>
    (resource?.offers || []).filter((offer) =>
      offer.offerKind !== 2
      && (!isSelfHostedResourceMode || isUnlimitedOffer(offer))),
  [isSelfHostedResourceMode, resource?.offers]);

  const selectedOffer = useMemo(() =>
    offers.find((offer) =>
      String(offer.key || "").toLowerCase() === String(selectedOfferKey || "").toLowerCase()
      && !(
        !isSelfHostedResourceMode
        && isUnlimitedOffer(offer)
        && getOwnedQuantity(clientProductOffers, offer?.key) >= 1
      )
    ) || null,
  [clientProductOffers, isSelfHostedResourceMode, offers, selectedOfferKey]);
  const selectedOfferHasPendingPayment = selectedOffer
    ? hasPendingClientOffer(clientProductOffers, selectedOffer.key)
    : false;
  const resolvedActionVerb = isSelfHostedResourceMode
    ? "Select"
    : "Buy";
  const resolvedPrimaryAction = selectedOfferHasPendingPayment
    ? "View Status"
    : resolvedActionVerb;
  const resolvedActionNoun = resolvedActionVerb.toLowerCase();

  useEffect(() => {
    if (!open) return;
    setSelectedOfferKey(getInitialOfferKey({
      offers,
      requestedOfferKey: initialOfferKey,
      clientProductOffers,
      isSelfHostedResourceMode
    }));
    setIsSaving(false);
    setActionError("");
  }, [clientProductOffers, initialOfferKey, isSelfHostedResourceMode, offers, open, resource?.key]);

  if (!open) {
    return null;
  }

  const handleClose = () => {
    if (isSaving) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!resource || !selectedOffer || isSaving) {
      return undefined;
    }

    setIsSaving(true);

    if (!isSelfHostedResourceMode) {
      if (selectedOfferHasPendingPayment) {
        openMarketplaceCheckoutPanel({
          offerKey: selectedOffer.key,
          resourceKey: resource.key,
          showPaymentFollowUp: true
        });
        onClose?.();
        setIsSaving(false);
        return undefined;
      }

      try {
        const saveResult = await beforeCheckout?.();
        if (saveResult?.Success === false) {
          throw new Error("Save the current editor successfully before continuing to checkout.");
        }
        openMarketplaceCheckoutPanel({
          offerKey: selectedOffer.key,
          resourceKey: resource.key
        });
        onClose?.();
      } catch (ex) {
        setActionError(ex?.message || "The current editor could not be saved before checkout.");
      } finally {
        setIsSaving(false);
      }
      return undefined;
    }

    const payload = {
      IsNew: true,
      EnvironmentKey: environmentKey || "",
      ProductKey: resource.productKey || product?.key || "",
      ProductResourceKey: resource.key,
      ProductResourceDefinitionId: resource.id || resource._id || resource.ProductResourceDefinitionId || "",
      ProductOfferKey: selectedOffer.key,
      ProductName: product?.name || "",
      ResourceName: resource.name || "",
      DisplayName: resource.name || "",
      Notes: ""
    };

    try {
      const result = await clientResourceMgr.RunOperation({
        operationName: Operations.ClientResourceItem.AddMarketplaceClientResource,
        item: payload,
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });

      if (result?.Success) {
        onPurchased?.(resource, getFirst(result.Items), result, selectedOffer);
        onClose?.();
        return result;
      }

      setActionError(getFailureMessage(result, "Allowance package could not be selected."));
      return result;
    } catch (ex) {
      setActionError(ex?.message || "Allowance package could not be selected.");
      return {
        Success: false,
        Messages: [{ DisplayMessage: ex?.message || "Allowance package could not be added." }]
      };
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
    <DialogControl
      openDialog={open}
      title={resource?.name ? `${resolvedActionVerb} allowance - ${resource.name}` : `${resolvedActionVerb} allowance`}
      subTitle={`Choose the package to ${resolvedActionNoun}. Package allowances vary by plan.`}
      okText={resolvedPrimaryAction}
      cancelText="Cancel"
      onCancel={handleClose}
      onX={handleClose}
      onOk={handleConfirm}
      disableOk={!selectedOffer || isSaving}
      loadingOk={isSaving}
      className="marketplace-dialog-control marketplace-allowance-dialog-control"
      innerclassName="marketplace-dialog-control__inner"
      controlOrigin="marketplace-allowance-purchase"
    >
        <div className="marketplace-allowance-dialog__plans" role="listbox" aria-label="Available plans">
          {offers.map((offer) => {
            const selected = selectedOffer?.key === offer.key;
            const ownedQuantity = getOwnedQuantity(clientProductOffers, offer.key);
            const pendingPayment = hasPendingClientOffer(clientProductOffers, offer.key);
            const disabled = !isSelfHostedResourceMode
              && isUnlimitedOffer(offer)
              && ownedQuantity >= 1;
            return (
              <ButtonControl
                key={offer.key}
                type="standard"
                className={`marketplace-allowance-dialog__plan ${selected ? "selected" : ""} ${disabled ? "disabled" : ""}`}
                onClick={() => {
                  if (!disabled) {
                    setSelectedOfferKey(offer.key);
                  }
                }}
                role="option"
                aria-selected={selected}
                aria-disabled={disabled}
                disabled={disabled}
              >
                <span className="marketplace-allowance-dialog__plan-main">
                  <strong>{offer.name}</strong>
                  <small>{offer.sku || offer.key}</small>
                </span>
                <span className="marketplace-allowance-dialog__plan-details">
                  <span className="marketplace-allowance-dialog__plan-price">{buildPricingDisplay(offer, isSelfHostedResourceMode)}</span>
                  <span className="marketplace-allowance-dialog__plan-grants">{buildGrantSummary(offer, resource?.key)}</span>
                  <span className="marketplace-allowance-dialog__plan-owned">
                    {pendingPayment ? "Pending Payment" : `Owned: ${ownedQuantity}`}
                  </span>
                </span>
              </ButtonControl>
            );
          })}
          {offers.length === 0 ? (
            <div className="marketplace-dialog-control__empty">No eligible allowance plans are available for this resource.</div>
          ) : null}
      </div>
    </DialogControl>
    <DialogControl
      controlOrigin="marketplace-allowance-purchase-error"
      title="Allowance Selection Error"
      openDialog={Boolean(actionError)}
      okText="Close"
      hideCancel
      onOk={() => setActionError("")}
      onCancel={() => setActionError("")}
      onX={() => setActionError("")}
    >
      <p>{actionError}</p>
    </DialogControl>
    </>
  );
};

export default MarketplaceAllowancePurchaseDialog;
